import { useRef } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { getProvePrefillDecision, useOptimizelyVariation, optimizelyVariationOptions, PROVE_PREFILL_FLAG, provePrefillAttributes } from 'merchant-api/vendor/optimizely'
import { MarketingPageData } from 'merchant-api/types'
import { analytics, BasicInfo, ProveBasicInfo } from 'merchant-components'

// The preapproval ref is a JWT whose `sub` is the application id. Only routing reads it here;
// authorization stays server-side (the /application guard challenges OTP for the application's
// stored email when the visitor holds no valid token).
const refApplicationId = (ref: string) => {
  try {
    const payload = JSON.parse(atob(ref.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sub === 'string' && payload.sub ? payload.sub : undefined;
  } catch {
    return undefined;
  }
};

// Direct-apply always creates DIRECT applications; the M3 audiences match on this attribute, so the
// landing decision must carry it or the visitor falls out of the rule that the wizard later reads.
const proveDecisionOptions = {
  featureKey: PROVE_PREFILL_FLAG,
  analytics,
  attributes: provePrefillAttributes('DIRECT'),
};

const LandingComponent = () => {
  const queryClient = useQueryClient();
  // Set once Prove is shown, so an opt-out isn't counted as a fresh direct_new visitor.
  const shownProve = useRef(false);
  const variationQueryOptions = optimizelyVariationOptions(proveDecisionOptions);
  const { data: variation } = useOptimizelyVariation(proveDecisionOptions);
  const decision = getProvePrefillDecision(variation);

  // M3 rule (XF-10207): Prove runs inside app intake as PROVE_STEP, so the entry Prove UI and its
  // /login/mobile detour are retired for everyone the rule covers — test and control alike.
  // Outside the rule, the legacy M1/M2 behaviour is unchanged.
  if (decision.isM3Rule || !decision.legacyEntryProve) {
    // After a Prove opt-out, skip the duplicate StepLoaded; FirstStepLoaded still fires.
    return <BasicInfo emitStepLoaded={!shownProve.current} />;
  }

  shownProve.current = true;
  return (
    <ProveBasicInfo
      onOptOut={() => {
        // Opt-out stays on BASIC_INFO, so it's not a step-1 exit (no stepOrder, direction 'same').
        analytics.track('StepChange', {
          fromStep: 'BASIC_INFO',
          toStep: 'BASIC_INFO',
          step: 'BASIC_INFO',
          flowType: 'direct_new',
          fromStepName: 'BASIC_INFO',
          toStepName: 'BASIC_INFO',
          transition: 'PROVE_ENTRY->MANUAL_ENTRY',
          direction: 'same',
          // Keep this synthetic bridge out of Adobe Launch and Optimizely; Datadog still records it.
          suppressBridgeTracking: true,
          subStep: 'PROVE_OPT_OUT',
        });
        // Step-1 entry for direct_new after a Prove opt-out.
        analytics.track('StepLoaded', {
          step: 'BASIC_INFO',
          stepOrder: 1,
          flowType: 'direct_new',
          subStep: 'POST_PROVE_OPT_OUT',
          suppressBridgeTracking: true,
        });
        queryClient.setQueryData(variationQueryOptions.queryKey, 'disabled');
      }}
    />
  );
};

export const Route = createFileRoute('/(public)/apply/landing')({
  component: LandingComponent,
  validateSearch: (search: Record<string, unknown>): MarketingPageData => {
    return {
      ref: search.ref as string,
    }
  },
  loaderDeps: ({ search }) => ({ ref: search.ref }),
  // The ref->redirect logic lives in `loader`, NOT `beforeLoad`, on purpose.
  // Throwing a redirect from an async beforeLoad on a hard page load races with
  // TanStack Router loading the ancestor matches: the ancestors are left `pending`
  // with no loadPromise, so the router throws `undefined` during render and the page
  // goes blank. Loaders run after all beforeLoads settle, which avoids the race.
  loader: async ({ deps, context }) => {
  if (deps.ref) {
    // An unreadable ref falls through to the plain intake landing form.
    const applicationId = refApplicationId(deps.ref);
    if (applicationId) throw redirect({ to: '/application/$id', params: { id: applicationId } });
  }
  await context.queryClient.ensureQueryData(optimizelyVariationOptions(proveDecisionOptions));
},
})
